import { TTheme } from '@rbx/ui';
import { CSS_SELECTORS, CSS_CLASS_NAMES, DATA_ATTRIBUTES } from './constants';
import { CurlCopyButtonTooltipData, HighRiskWarningDialog } from './types';
import { createCurlCopyButtonClickHandler } from './utils';
import RiskLevel from '../../types/RiskLevel';
import TryItOutAuthMode from '../../types/TryItOutAuthMode';

/**
 * Gets the try-it-out header within an operation element
 */
export const getTryItOutHeader = (operationElement: Element | null): Element | null => {
  return operationElement?.querySelector(`.${CSS_CLASS_NAMES.TRY_IT_OUT_HEADER}`) || null;
};

/**
 * Gets the try it out button within an operation element
 */
export const getTryItOutButton = (operationElement: Element | null): Element | null => {
  return operationElement?.querySelector(`.${CSS_CLASS_NAMES.TRY_IT_OUT_BUTTON}`) || null;
};

/**
 * Gets the cURL code block within an element
 */
export const getCurlCodeBlock = (element: Element | null): Element | null => {
  return element?.querySelector('pre.curl') || null;
};

/**
 * Gets the operation element (.opblock) that contains a given element
 */
export const getOperationElement = (element: Element | null): Element | null => {
  return element?.closest('.opblock') || null;
};

/**
 * Gets the Swagger UI try-it-out button (not our try it out button) within an element
 */
export const getSwaggerUITryItOutButton = (element: Element | null): Element | null => {
  return (
    element?.querySelector(
      `${CSS_SELECTORS.SWAGGER_UI_TRY_IT_OUT_BUTTON}:not(.${CSS_CLASS_NAMES.TRY_IT_OUT_BUTTON})`,
    ) || null
  );
};

/**
 * Gets the opblock-summary element within an operation element
 */
export const getOperationSummary = (operationElement: Element | null): Element | null => {
  return operationElement?.querySelector('.opblock-summary') || null;
};

/**
 * Creates a try it out button that mimics the original Swagger UI "Try it out" button
 */
export const createTryItOutButton = (originalButton: Element, theme: TTheme): HTMLButtonElement => {
  const buttonElement = document.createElement('button');
  buttonElement.className = `${CSS_CLASS_NAMES.TRY_IT_OUT_BUTTON} ${originalButton.className}`;
  buttonElement.textContent = originalButton.textContent;
  buttonElement.style.borderColor = theme.palette.content.disabled;
  return buttonElement;
};

/**
 * Processes all Swagger UI buttons within an element
 */
export const processSwaggerUIButtons = (
  element: Element | null,
  processor: (button: Element) => void,
): void => {
  const allSwaggerUIButtons = element?.querySelectorAll('button.btn, .copy-to-clipboard') || [];
  allSwaggerUIButtons.forEach(processor);
};

/**
 * Checks if an element is a Swagger UI try-it-out button (not our try it out button)
 */
export const isSwaggerUITryItOutButton = (element: Element | null): boolean => {
  return (
    element?.matches(
      `${CSS_SELECTORS.SWAGGER_UI_TRY_IT_OUT_BUTTON}:not(.${CSS_CLASS_NAMES.TRY_IT_OUT_BUTTON})`,
    ) ?? false
  );
};

/**
 * Checks if an element is a Swagger UI try-it-out cancel button (try-it-out mode active)
 */
export const isSwaggerUITryItOutCancelButton = (element: Element | null): boolean => {
  return element?.matches(`${CSS_SELECTORS.SWAGGER_UI_TRY_IT_OUT_BUTTON}.cancel`) ?? false;
};

/**
 * Checks if an element is a Swagger UI Execute button
 */
export const isSwaggerUIExecuteButton = (element: Element | null): boolean => {
  return element?.matches('button.btn.execute.opblock-control__btn') ?? false;
};

/**
 * Checks if an element is a Swagger UI body param cancel button
 */
export const isSwaggerUIBodyParamCancelButton = (element: Element | null): boolean => {
  return element?.matches(CSS_SELECTORS.BODY_PARAM_CANCEL_BUTTON) ?? false;
};

/**
 * Creates the main header div for the "Try it out" section
 */
export const createHeaderDiv = (theme: TTheme): HTMLDivElement => {
  const headerDiv = document.createElement('div');
  headerDiv.className = CSS_CLASS_NAMES.TRY_IT_OUT_HEADER;
  headerDiv.style.display = 'flex';
  headerDiv.style.justifyContent = 'space-between';
  headerDiv.style.alignItems = 'center';
  headerDiv.style.padding = '16px';
  headerDiv.style.borderBottom = `1px solid ${theme.palette.components.divider}`;
  return headerDiv;
};

/**
 * Creates the "Try it out" heading text for the "Try it out" header
 */
export const createHeaderTextElement = (
  theme: TTheme,
  isTryItOutEnabled: boolean,
  headerText: string,
): HTMLHeadingElement => {
  const headerTextElement = document.createElement('h4');
  headerTextElement.textContent = headerText;
  headerTextElement.style.margin = '0';
  headerTextElement.style.paddingLeft = '4px';
  headerTextElement.style.fontSize = theme.typography.h5.fontSize || '20px';
  headerTextElement.style.fontWeight = String(theme.typography.fontWeightBold || 800);

  if (!isTryItOutEnabled) {
    headerTextElement.style.color = theme.palette.content.disabled;
  }

  return headerTextElement;
};

/**
 * Processes a Swagger UI body param cancel button by applying custom styling
 */
export const processBodyParamCancelButton = (
  button: Element,
  theme: TTheme,
  cancelText: string,
): void => {
  if (!isSwaggerUIBodyParamCancelButton(button)) return;

  const buttonElement = button as HTMLElement;

  buttonElement.textContent = cancelText;
  buttonElement.style.borderColor = theme.palette.content.disabled;
  // Override CSS that hides these buttons by default (prevents red flash on initial render)
  buttonElement.style.opacity = '1';
  buttonElement.style.pointerEvents = 'auto';
};

/**
 * Adds a tooltip to a specific cURL copy button by replacing it with a portal container
 */
export const addCurlCopyButtonTooltip = (
  copyButton: Element,
  setCurlCopyButtonTooltipData: (data: CurlCopyButtonTooltipData) => void,
  method: string,
  url: string,
  riskLevel: RiskLevel,
): void => {
  if (
    !copyButton.classList.contains('copy-to-clipboard') ||
    !copyButton.parentElement?.classList.contains('curl-command')
  ) {
    return;
  }

  const tooltipContainer = document.createElement('div');
  const buttonClasses = copyButton.className;
  const curlCopyButtonClickHandler = createCurlCopyButtonClickHandler(
    tooltipContainer,
    method,
    url,
    riskLevel,
  );

  copyButton.parentNode?.insertBefore(tooltipContainer, copyButton);
  copyButton.remove();

  setCurlCopyButtonTooltipData({
    container: tooltipContainer,
    classes: buttonClasses,
    clickHandler: curlCopyButtonClickHandler,
  });
};

/**
 * Extracts operation info (path and method) from an opblock element
 */
export const extractOperationInfo = (
  operationElement: Element,
): { method: string; path: string } | null => {
  const methodElement = operationElement.querySelector('.opblock-summary-method');
  const pathElement = operationElement.querySelector('.opblock-summary-path');

  if (!methodElement || !pathElement) {
    return null;
  }

  return {
    method: methodElement.textContent?.trim().toUpperCase() || '',
    path: pathElement.textContent?.trim() || '',
  };
};

/**
 * Checks if request matches the operation (path and method)
 */
export const requestMatchesOperation = (
  requestMethod: string,
  requestPath: string,
  operationInfo: { method: string; path: string },
): boolean => {
  const methodMatches = requestMethod === operationInfo.method;
  // Replace {param} with [^/]+ to match any non-slash characters
  const pathPattern = operationInfo.path.replace(/\{[^}]+\}/g, '[^/]+');
  const pathRegex = new RegExp(pathPattern);
  const pathMatches = pathRegex.test(requestPath);

  return methodMatches && pathMatches;
};

/**
 * Gets the selected auth mode from an auth mode selector
 */
export const getSelectedAuthMode = (authModeSelector: Element): TryItOutAuthMode => {
  const selectedElement = authModeSelector.querySelector(`[${DATA_ATTRIBUTES.SELECTED}="true"]`);
  const rawAuthMode = selectedElement?.getAttribute(DATA_ATTRIBUTES.AUTH_MODE);

  if (rawAuthMode && Object.values(TryItOutAuthMode).includes(rawAuthMode as TryItOutAuthMode)) {
    return rawAuthMode as TryItOutAuthMode;
  }

  return TryItOutAuthMode.NONE;
};

/**
 * Gets auth mode selector and operation path for a specific request method and path
 */
export const getAuthModeSelectorAndPath = (
  requestMethod: string,
  requestPath: string,
): {
  authModeSelector: Element | null;
  operationPath: string;
} => {
  const authModeSelectors = document.querySelectorAll(`.${CSS_CLASS_NAMES.AUTH_MODE_SELECTOR}`);

  let operationPath: string = '';

  const authModeSelector =
    Array.from(authModeSelectors).find((selector) => {
      const operationElement = getOperationElement(selector);
      if (!operationElement) return false;

      const operationInfo = extractOperationInfo(operationElement);
      if (!operationInfo) return false;

      const isMatch = requestMatchesOperation(requestMethod, requestPath, operationInfo);
      if (isMatch) {
        operationPath = operationInfo.path;
      }
      return isMatch;
    }) || null;

  return {
    authModeSelector,
    operationPath,
  };
};

/**
 * Processes Execute buttons to add high-risk warning interception
 */
export const processExecuteButton = (
  element: Element,
  highRiskWarningDialog: HighRiskWarningDialog,
  setLastClickedButton: (element: HTMLElement) => void,
  setShowHighRiskWarningDialog: (show: boolean) => void,
): void => {
  if (!isSwaggerUIExecuteButton(element)) {
    return;
  }

  const clickHandler = (event: Event) => {
    if (!highRiskWarningDialog.isAccepted()) {
      event.preventDefault();
      event.stopPropagation();

      setLastClickedButton(element as HTMLElement);
      // Blur the button to remove focus before opening dialog (prevents aria-hidden violation)
      (element as HTMLElement).blur();
      setShowHighRiskWarningDialog(true);
    }
  };

  element.addEventListener('click', clickHandler);
};
